import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import getEnvValue from 'commons/getEnvValue';
import { logError } from 'commons/logger';
import useRandomId from 'hooks/useRandomId';
import { setOptions, importLibrary } from 'components/consumer/StoreLocatorPage/loadGoogleMaps';
const GOOGLE_MAPS_API_KEY = getEnvValue('NEXT_PUBLIC_GOOGLE_MAPS_API_KEY');
const CITY_PLACE_TYPES = ['locality', 'administrative_area_level_3'];
const PLACE_DETAILS_FIELDS = ['addressComponents', 'formattedAddress', 'location'];
const MIN_INPUT_LENGTH = 3;
const DEBOUNCE_MS = 500;
const BLUR_CLOSE_DELAY_MS = 150;
const ALL_DIGITS_REGEX = /^\d+$/;
const getAddressComponent = (place, type) => place?.addressComponents?.find(component => component.types.includes(type));
const buildPlaceLabel = place => {
  const city = getAddressComponent(place, 'locality')?.longText;
  const stateShortName = getAddressComponent(place, 'administrative_area_level_1')?.shortText;
  const cityState = [city, stateShortName].filter(Boolean).join(', ');
  return cityState || place.formattedAddress || '';
};
const useCityStatePlacesAutocomplete = ({
  inputRef,
  onPlaceSelected
}) => {
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  onPlaceSelectedRef.current = onPlaceSelected;
  const listboxId = useRandomId('store-locator-city-suggestions');
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const isOpen = dropdownPosition !== null && suggestions.length > 0;
  const placesLibraryRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const blurTimerRef = useRef(null);
  const latestRequestIdRef = useRef(0);
  const suggestionsRef = useRef(suggestions);
  suggestionsRef.current = suggestions;
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;
  const mountedRef = useRef(true);
  const closeDropdown = useCallback(() => {
    setSuggestions([]);
    setActiveIndex(-1);
    setDropdownPosition(null);
  }, []);
  const positionDropdown = useCallback(() => {
    const inputEl = inputRef.current;
    if (!inputEl) return;
    const rect = inputEl.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom,
      left: rect.left,
      width: rect.width
    });
  }, [inputRef]);
  const selectSuggestion = useCallback(async index => {
    const placePrediction = suggestionsRef.current[index]?.placePrediction;
    closeDropdown();
    if (!placePrediction) return;
    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: PLACE_DETAILS_FIELDS
      });
      if (!mountedRef.current) return;
      const {
        location
      } = place;
      if (!location) return;
      onPlaceSelectedRef.current?.({
        lat: location.lat(),
        lng: location.lng(),
        label: buildPlaceLabel(place)
      });
    } catch (err) {
      if (!mountedRef.current) return;
      logError(err, 'Failed to fetch Google Place details for store locator search');
    } finally {
      const AutocompleteSessionToken = placesLibraryRef.current?.AutocompleteSessionToken;
      if (AutocompleteSessionToken) {
        sessionTokenRef.current = new AutocompleteSessionToken();
      }
    }
  }, [closeDropdown]);
  const fetchSuggestions = useCallback(async input => {
    const AutocompleteSuggestion = placesLibraryRef.current?.AutocompleteSuggestion;
    if (!AutocompleteSuggestion) return;
    const requestId = ++latestRequestIdRef.current;
    try {
      const {
        suggestions: results
      } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: sessionTokenRef.current,
        includedPrimaryTypes: CITY_PLACE_TYPES,
        includedRegionCodes: ['us'],
        language: 'en-US'
      });
      if (!mountedRef.current || requestId !== latestRequestIdRef.current) {
        return;
      }
      if (results?.length > 0) {
        setSuggestions(results);
        setActiveIndex(-1);
        positionDropdown();
      } else {
        closeDropdown();
      }
    } catch (err_0) {
      if (!mountedRef.current || requestId !== latestRequestIdRef.current) {
        return;
      }
      logError(err_0, 'Failed to fetch Google Places Autocomplete suggestions for store locator search');
      closeDropdown();
    }
  }, [closeDropdown, positionDropdown]);
  const handleInput = useCallback(event => {
    clearTimeout(debounceTimerRef.current);
    const {
      value
    } = event.target;
    const trimmed = value.trim();
    if (trimmed.length < MIN_INPUT_LENGTH || ALL_DIGITS_REGEX.test(trimmed)) {
      closeDropdown();
      return;
    }
    debounceTimerRef.current = setTimeout(() => fetchSuggestions(trimmed), DEBOUNCE_MS);
  }, [closeDropdown, fetchSuggestions]);
  const handleKeyDown = useCallback(event_0 => {
    const count = suggestionsRef.current.length;
    if (count === 0) return;
    if (event_0.key === 'ArrowDown') {
      event_0.preventDefault();
      setActiveIndex(current => (current + 1) % count);
    } else if (event_0.key === 'ArrowUp') {
      event_0.preventDefault();
      setActiveIndex(current_0 => (current_0 - 1 + count) % count);
    } else if (event_0.key === 'Enter' && activeIndexRef.current >= 0) {
      event_0.preventDefault();
      selectSuggestion(activeIndexRef.current);
    } else if (event_0.key === 'Escape') {
      closeDropdown();
    }
  }, [selectSuggestion, closeDropdown]);
  const handleBlur = useCallback(() => {
    clearTimeout(debounceTimerRef.current);
    clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(closeDropdown, BLUR_CLOSE_DELAY_MS);
  }, [closeDropdown]);
  const handleViewportChange = useCallback(() => {
    if (suggestionsRef.current.length > 0) {
      positionDropdown();
    }
  }, [positionDropdown]);

  // Loads the Places SDK once on mount.
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return undefined;
    let cancelled = false;
    setOptions({
      key: GOOGLE_MAPS_API_KEY,
      v: 'quarterly'
    });
    importLibrary('places').then(placesLib => {
      if (cancelled) return;
      placesLibraryRef.current = placesLib;
      sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
    }).catch(err_1 => {
      logError(err_1, 'Failed to load Google Places Autocomplete for store locator search');
    });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(debounceTimerRef.current);
      clearTimeout(blurTimerRef.current);
    };
  }, []);
  useEffect(() => {
    const inputEl_0 = inputRef.current;
    if (!inputEl_0) return undefined;
    inputEl_0.setAttribute('role', 'combobox');
    inputEl_0.setAttribute('aria-autocomplete', 'list');
    inputEl_0.setAttribute('aria-controls', listboxId);
    inputEl_0.addEventListener('input', handleInput);
    inputEl_0.addEventListener('keydown', handleKeyDown);
    inputEl_0.addEventListener('blur', handleBlur);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      inputEl_0.removeEventListener('input', handleInput);
      inputEl_0.removeEventListener('keydown', handleKeyDown);
      inputEl_0.removeEventListener('blur', handleBlur);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      inputEl_0.removeAttribute('role');
      inputEl_0.removeAttribute('aria-autocomplete');
      inputEl_0.removeAttribute('aria-controls');
      inputEl_0.removeAttribute('aria-expanded');
      inputEl_0.removeAttribute('aria-activedescendant');
    };
  }, [inputRef, listboxId, handleInput, handleKeyDown, handleBlur, handleViewportChange]);
  useEffect(() => {
    const inputEl_1 = inputRef.current;
    if (!inputEl_1) return;
    inputEl_1.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    if (isOpen && activeIndex >= 0) {
      inputEl_1.setAttribute('aria-activedescendant', `${listboxId}-option-${activeIndex}`);
    } else {
      inputEl_1.removeAttribute('aria-activedescendant');
    }
  }, [inputRef, listboxId, isOpen, activeIndex]);
  const suggestionsDropdownProps = useMemo(() => isOpen ? {
    listboxId,
    dropdownPosition,
    suggestions,
    activeIndex,
    onSelectSuggestion: selectSuggestion,
    onHoverSuggestion: setActiveIndex
  } : null, [isOpen, listboxId, dropdownPosition, suggestions, activeIndex, selectSuggestion]);
  return suggestionsDropdownProps;
};
export default useCityStatePlacesAutocomplete;